import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/app/auth.service';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private handlingUnauthorized = false;

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && this.shouldHandleUnauthorized(request)) {
          void this.handleUnauthorized(error);
        }

        return throwError(() => error);
      })
    );
  }

  private shouldHandleUnauthorized(request: HttpRequest<unknown>): boolean {
    if (this.handlingUnauthorized) {
      return false;
    }

    const currentUrl = this.router.url.split('?')[0];
    const authRoutes = ['/login', '/subscriber-login', '/blog-login'];
    if (authRoutes.some((route) => currentUrl.startsWith(route))) {
      return false;
    }

    const url = request.url.toLowerCase();
    return !(
      url.includes('/auth/') ||
      url.includes('/userlogin/') ||
      url.includes('authenticate')
    );
  }

  private async handleUnauthorized(error: HttpErrorResponse): Promise<void> {
    if (this.handlingUnauthorized) {
      return;
    }

    this.handlingUnauthorized = true;

    const detail =
      this.extractErrorMessage(error) ??
      'Your session has expired. Please log in again.';

    this.messageService.add({
      severity: 'warn',
      summary: 'Session Expired',
      detail,
      life: 6000,
    });

    try {
      await this.authService.handleSessionExpired();
    } finally {
      setTimeout(() => {
        this.handlingUnauthorized = false;
      }, 1000);
    }
  }

  private extractErrorMessage(error: HttpErrorResponse): string | null {
    const body = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }

    if (body && typeof body === 'object') {
      const message = (body as { error?: string; message?: string }).error
        ?? (body as { error?: string; message?: string }).message;

      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }

    return null;
  }
}
